// =============================================================================
// API Client for Log Collector
// =============================================================================

/**
 * Log level types
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

/**
 * API Key permission types
 */
export type ApiKeyPermission = 'write' | 'read' | 'admin';

/**
 * Log entry as returned from the API
 */
export interface LogEntry {
  id: string;
  ts: string;
  level: LogLevel;
  sub_app: string | null;
  instance_id: string | null;
  correlation_id: string | null;
  session_id: string | null;
  message: string;
  context: Record<string, unknown> | null;
  created_at: string;
}

/**
 * App as returned from the API
 */
export interface App {
  id: string;
  name: string;
  created_at: string;
}

/**
 * API Key as returned from the API (without the full key)
 */
export interface ApiKey {
  id: string;
  key_prefix: string;
  app_id: string;
  permissions: ApiKeyPermission;
  rate_limit: number;
  created_at: string;
}

/**
 * API Key with full key (only returned on creation)
 */
export interface ApiKeyWithKey extends ApiKey {
  key: string;
}

/**
 * System statistics
 */
export interface SystemStats {
  apps_count: number;
  api_keys_count: number;
  total_logs: number;
  logs_by_app: Array<{
    app_id: string;
    app_name: string;
    log_count: number;
    db_size_bytes: number;
  }>;
}

/**
 * Search request parameters
 */
export interface SearchParams {
  from: string;
  to: string;
  sub_apps?: string[];
  instance_ids?: string[];
  session_ids?: string[];
  correlation_ids?: string[];
  levels?: LogLevel[];
  match?: string;
  regex?: string;
  context?: Record<string, unknown>;
  idx1?: string; // Environment include filter
  idx1_exclude?: string[]; // Environment exclude filter (e.g., exclude "dev")
  limit?: number;
  offset?: number;
}

/**
 * Search response
 */
export interface SearchResponse {
  logs: LogEntry[];
  total: number;
  has_more: boolean;
}

/**
 * Clear logs response
 */
export interface ClearLogsResponse {
  success: boolean;
  deleted: number;
  message: string;
}

/**
 * Log type for categorization
 */
export type LogType = 'log' | 'event' | 'timing' | 'metric' | 'http' | 'job';

/**
 * Time bucket size options
 */
export type TimeBucket = '1m' | '5m' | '15m' | '1h' | '6h' | '1d';

/**
 * Stats summary response
 */
export interface StatsSummary {
  timeRange: { from: string; to: string };
  totals: {
    logs: number;
    events: number;
    errors: number;
  };
  byLevel: Record<string, number>;
  byType: Record<string, number>;
  bySubApp: Record<string, number>;
  topMessages: Array<{ message: string; count: number }>;
}

/**
 * Timeline bucket
 */
export interface TimelineBucket {
  start: string;
  end: string;
  counts: {
    total: number;
    byLevel: Record<string, number>;
    byType: Record<string, number>;
    bySubApp?: Record<string, number>;
  };
}

/**
 * Timeline response
 */
export interface TimelineResponse {
  bucketSize: TimeBucket;
  buckets: TimelineBucket[];
}

/**
 * Export format types
 */
export type ExportFormat = 'json' | 'txt' | 'csv';

/**
 * Aggregation metric configuration
 */
export interface AggregationMetric {
  field: string;
  agg: 'count' | 'count_distinct' | 'sum' | 'avg' | 'min' | 'max' | 'p50' | 'p95' | 'p99' | 'error_count' | 'error_rate';
  alias: string;
}

/**
 * Aggregation configuration input for create/update
 */
export interface AggregationConfigInput {
  name: string;
  sub_app_pattern?: string;
  type_pattern?: string;
  message_pattern?: string;
  group_by?: string[];
  metrics?: AggregationMetric[];
  time_bucket?: TimeBucket;
  enabled?: boolean;
}

/**
 * Aggregation configuration as returned from API
 */
export interface AggregationConfig {
  id: string;
  app_id: string;
  name: string;
  sub_app_pattern: string | null;
  type_pattern: string | null;
  message_pattern: string | null;
  group_by: string[];
  metrics: AggregationMetric[];
  time_bucket: TimeBucket;
  enabled: boolean;
  created_at: string;
}

/**
 * Aggregation result from compute
 */
export interface AggregationResult {
  bucket_start: string;
  bucket_end: string;
  dimensions: Record<string, string | number | boolean | null>;
  metrics: Record<string, number>;
}

/**
 * Dynamic filters for aggregation compute
 */
export interface AggregationFilters {
  levels?: Array<'debug' | 'info' | 'warn' | 'error' | 'fatal'>;
  sub_apps?: string[];
  types?: Array<'log' | 'event' | 'timing' | 'metric' | 'http' | 'job' | 'bug_report'>;
  instance_ids?: string[];
  context?: Record<string, string | number | boolean>;
  message_match?: string;
}

/**
 * Aggregation compute response
 */
export interface AggregationComputeResponse {
  config: AggregationConfig;
  timeRange: { from: string; to: string };
  filters: AggregationFilters | null;
  results: AggregationResult[];
}

/**
 * Login response
 */
export interface LoginResponse {
  success: boolean;
  token?: string;
  error?: string;
}

/**
 * API error response
 */
export interface ApiError {
  error: string;
  message?: string;
  details?: unknown;
}

/**
 * API client configuration
 */
export interface ApiClientConfig {
  baseUrl: string;
  sessionToken?: string;
  apiKey?: string;
  /** Callback fired when server returns 401 Unauthorized */
  onUnauthorized?: () => void;
}

/**
 * API Client for communicating with the log-collector server.
 * Supports both session-based auth (for admin UI) and API key auth (for log queries).
 */
export class ApiClient {
  private baseUrl: string;
  private sessionToken: string | null = null;
  private apiKey: string | null = null;
  private onUnauthorized?: () => void;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, ''); // Remove trailing slash
    this.sessionToken = config.sessionToken ?? null;
    this.apiKey = config.apiKey ?? null;
    this.onUnauthorized = config.onUnauthorized;
  }

  /**
   * Set the callback for unauthorized responses.
   */
  setOnUnauthorized(callback: (() => void) | undefined): void {
    this.onUnauthorized = callback;
  }

  /**
   * Set the session token for admin authentication.
   */
  setSessionToken(token: string | null): void {
    this.sessionToken = token;
  }

  /**
   * Set the API key for log queries.
   */
  setApiKey(key: string | null): void {
    this.apiKey = key;
  }

  /**
   * Get the current session token.
   */
  getSessionToken(): string | null {
    return this.sessionToken;
  }

  /**
   * Check if authenticated.
   */
  isAuthenticated(): boolean {
    return this.sessionToken !== null || this.apiKey !== null;
  }

  /**
   * Build headers for API requests.
   */
  private buildHeaders(includeAuth = true): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (includeAuth) {
      if (this.sessionToken) {
        headers['Authorization'] = `Bearer ${this.sessionToken}`;
      } else if (this.apiKey) {
        headers['X-API-Key'] = this.apiKey;
      }
    }

    return headers;
  }

  /**
   * Make a request to the API.
   */
  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    includeAuth = true,
    extraHeaders?: Record<string, string>
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const headers = { ...this.buildHeaders(includeAuth), ...extraHeaders };
    const options: RequestInit = {
      method,
      headers,
      cache: 'no-store', // Prevent browser caching of API responses
    };

    if (body !== undefined) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);
    const data = await response.json();

    if (!response.ok) {
      // Handle 401 Unauthorized - trigger callback before throwing
      if (response.status === 401 && this.onUnauthorized) {
        this.onUnauthorized();
      }

      const error = data as ApiError;
      throw new ApiClientError(
        error.error || 'Unknown error',
        response.status,
        error
      );
    }

    return data as T;
  }

  // =============================================================================
  // Authentication
  // =============================================================================

  /**
   * Login with admin password.
   */
  async login(password: string): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>(
      'POST',
      '/superadmin/login',
      { password },
      false
    );

    if (response.success && response.token) {
      this.sessionToken = response.token;
    }

    return response;
  }

  /**
   * Logout and clear session.
   */
  async logout(): Promise<void> {
    if (this.sessionToken) {
      try {
        await this.request('POST', '/superadmin/logout', undefined, true);
      } catch {
        // Ignore logout errors
      }
    }
    this.sessionToken = null;
  }

  // =============================================================================
  // Apps
  // =============================================================================

  /**
   * List all apps.
   */
  async listApps(): Promise<App[]> {
    const response = await this.request<{ apps: App[] }>('GET', '/superadmin/apps');
    return response.apps;
  }

  /**
   * Create a new app.
   */
  async createApp(name: string): Promise<App> {
    const response = await this.request<{ app: App }>('POST', '/superadmin/apps', {
      name,
    });
    return response.app;
  }

  /**
   * Delete an app.
   */
  async deleteApp(appId: string): Promise<void> {
    await this.request('DELETE', `/superadmin/apps/${appId}`);
  }

  // =============================================================================
  // API Keys
  // =============================================================================

  /**
   * List API keys, optionally filtered by app.
   */
  async listApiKeys(appId?: string): Promise<ApiKey[]> {
    const query = appId ? `?app_id=${appId}` : '';
    const response = await this.request<{ api_keys: ApiKey[] }>(
      'GET',
      `/superadmin/api-keys${query}`
    );
    return response.api_keys;
  }

  /**
   * Create a new API key.
   */
  async createApiKey(
    appId: string,
    permissions: ApiKeyPermission = 'write',
    rateLimit = 1000
  ): Promise<ApiKeyWithKey> {
    const response = await this.request<{ api_key: ApiKeyWithKey }>(
      'POST',
      '/superadmin/api-keys',
      {
        app_id: appId,
        permissions,
        rate_limit: rateLimit,
      }
    );
    return response.api_key;
  }

  /**
   * Delete an API key.
   */
  async deleteApiKey(keyId: string): Promise<void> {
    await this.request('DELETE', `/superadmin/api-keys/${keyId}`);
  }

  // =============================================================================
  // Stats
  // =============================================================================

  /**
   * Get system statistics.
   */
  async getStats(): Promise<SystemStats> {
    return this.request<SystemStats>('GET', '/superadmin/stats');
  }

  /**
   * Get stats summary for an app.
   */
  async getStatsSummary(appId: string, from: string, to: string): Promise<StatsSummary> {
    const query = `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    return this.request<StatsSummary>(
      'GET',
      `/superadmin/apps/${appId}/stats/summary${query}`
    );
  }

  /**
   * Get stats timeline for an app.
   */
  async getStatsTimeline(
    appId: string,
    from: string,
    to: string,
    bucket?: TimeBucket
  ): Promise<TimelineResponse> {
    let query = `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    if (bucket) {
      query += `&bucket=${bucket}`;
    }
    return this.request<TimelineResponse>(
      'GET',
      `/superadmin/apps/${appId}/stats/timeline${query}`
    );
  }

  // =============================================================================
  // Search
  // =============================================================================

  /**
   * Search logs for a specific app.
   */
  async searchLogs(params: SearchParams, appId: string): Promise<SearchResponse> {
    return this.request<SearchResponse>('POST', '/search', params, true, {
      'X-App-ID': appId,
    });
  }

  // =============================================================================
  // Health
  // =============================================================================

  /**
   * Check server health.
   */
  async health(): Promise<{ status: string }> {
    return this.request<{ status: string }>('GET', '/health', undefined, false);
  }

  // =============================================================================
  // Log Management (Clear & Export)
  // =============================================================================

  /**
   * Clear all logs for an app.
   */
  async clearAllLogs(appId: string): Promise<ClearLogsResponse> {
    return this.request<ClearLogsResponse>(
      'DELETE',
      `/superadmin/apps/${appId}/logs`
    );
  }

  /**
   * Clear logs with filters for an app.
   */
  async clearLogs(appId: string, params: SearchParams): Promise<ClearLogsResponse> {
    return this.request<ClearLogsResponse>(
      'POST',
      `/superadmin/apps/${appId}/logs/clear`,
      params
    );
  }

  /**
   * Export logs for an app.
   * Returns a Blob that can be downloaded.
   */
  async exportLogs(
    appId: string,
    format: ExportFormat,
    params: { from: string; to: string } & Partial<Omit<SearchParams, 'from' | 'to' | 'limit' | 'offset'>>
  ): Promise<{ blob: Blob; filename: string; logCount: number }> {
    const queryParams = new URLSearchParams();
    queryParams.set('format', format);
    queryParams.set('from', params.from);
    queryParams.set('to', params.to);

    if (params.levels && params.levels.length > 0) {
      params.levels.forEach((level) => queryParams.append('levels', level));
    }
    if (params.sub_apps && params.sub_apps.length > 0) {
      params.sub_apps.forEach((app) => queryParams.append('sub_apps', app));
    }
    if (params.match) {
      queryParams.set('match', params.match);
    }
    if (params.regex) {
      queryParams.set('regex', params.regex);
    }

    const url = `${this.baseUrl}/superadmin/apps/${appId}/logs/export?${queryParams.toString()}`;
    const headers = this.buildHeaders(true);

    const response = await fetch(url, { headers });

    if (!response.ok) {
      const errorData = await response.json();
      throw new ApiClientError(
        errorData.error || 'Export failed',
        response.status,
        errorData
      );
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition') || '';
    const logCount = parseInt(response.headers.get('X-Log-Count') || '0', 10);

    // Extract filename from Content-Disposition header
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `logs-${appId}.${format}`;

    return { blob, filename, logCount };
  }

  // =============================================================================
  // Aggregation Config
  // =============================================================================

  /**
   * List aggregation configs for an app.
   */
  async listAggregations(appId: string): Promise<AggregationConfig[]> {
    const response = await this.request<{ configs: AggregationConfig[] }>(
      'GET',
      `/superadmin/apps/${appId}/aggregations`
    );
    return response.configs;
  }

  /**
   * Get a single aggregation config.
   */
  async getAggregation(appId: string, configId: string): Promise<AggregationConfig> {
    const response = await this.request<{ config: AggregationConfig }>(
      'GET',
      `/superadmin/apps/${appId}/aggregations/${configId}`
    );
    return response.config;
  }

  /**
   * Create an aggregation config.
   */
  async createAggregation(appId: string, config: AggregationConfigInput): Promise<AggregationConfig> {
    const response = await this.request<{ config: AggregationConfig }>(
      'POST',
      `/superadmin/apps/${appId}/aggregations`,
      config
    );
    return response.config;
  }

  /**
   * Update an aggregation config.
   */
  async updateAggregation(
    appId: string,
    configId: string,
    config: Partial<AggregationConfigInput>
  ): Promise<AggregationConfig> {
    const response = await this.request<{ config: AggregationConfig }>(
      'PUT',
      `/superadmin/apps/${appId}/aggregations/${configId}`,
      config
    );
    return response.config;
  }

  /**
   * Delete an aggregation config.
   */
  async deleteAggregation(appId: string, configId: string): Promise<void> {
    await this.request('DELETE', `/superadmin/apps/${appId}/aggregations/${configId}`);
  }

  /**
   * Compute aggregation results with optional filters.
   */
  async computeAggregation(
    appId: string,
    configId: string,
    from: string,
    to: string,
    filters?: AggregationFilters
  ): Promise<AggregationComputeResponse> {
    return this.request<AggregationComputeResponse>(
      'POST',
      `/superadmin/apps/${appId}/aggregations/${configId}/compute`,
      { from, to, filters }
    );
  }

  /**
   * Helper to trigger a file download in the browser.
   */
  downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

/**
 * Custom error class for API errors.
 */
export class ApiClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public response: ApiError
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}
